<script setup>
// ContactHeader.vue — toolbar densa estilo ClickUp pra /contacts.
// Layout (LTR):
//   [busca largura fluida (com chip de filtro ativo se houver)]
//   [filter] [save-segment | delete-segment] [sort] [more] | [Enviar mensagem]
//
// PageHeader externo (PageLayout > #header) ja renderiza o titulo
// da pagina, entao esse header NAO repete titulo — fica so a toolbar.
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ContactSortMenu from './components/ContactSortMenu.vue';
import ContactMoreActions from './components/ContactMoreActions.vue';

defineProps({
  showSearch: { type: Boolean, default: true },
  searchValue: { type: String, default: '' },
  activeSort: { type: String, default: 'last_activity_at' },
  activeOrdering: { type: String, default: '' },
  isSegmentsView: { type: Boolean, default: false },
  hasActiveFilters: { type: Boolean, default: false },
  isLabelView: { type: Boolean, default: false },
  isActiveView: { type: Boolean, default: false },
});

const emit = defineEmits([
  'search',
  'filter',
  'update:sort',
  'add',
  'import',
  'export',
  'createSegment',
  'deleteSegment',
]);
</script>

<template>
  <header
    class="sticky top-0 z-20 flex items-center justify-between gap-3 px-4 h-12 border-b border-n-weak bg-n-background"
  >
    <!-- Esquerda: busca (largura fluida ate 24rem) -->
    <div v-if="showSearch" class="relative flex-1 max-w-sm">
      <Icon
        icon="i-lucide-search"
        class="absolute top-1/2 -translate-y-1/2 ltr:left-2.5 rtl:right-2.5 text-n-slate-11 size-3.5 pointer-events-none z-10"
      />
      <Input
        :model-value="searchValue"
        type="search"
        :placeholder="$t('CONTACTS_LAYOUT.HEADER.SEARCH_PLACEHOLDER')"
        :custom-input-class="[
          '!h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 rtl:!pr-8 !py-1 !text-sm',
        ]"
        class="w-full"
        @input="emit('search', $event.target.value)"
      />
    </div>
    <div v-else class="flex-1" />

    <!-- Direita: actions + CTA -->
    <div class="flex items-center gap-1 shrink-0">
      <div v-if="!isLabelView && !isActiveView" class="relative">
        <Button
          id="toggleContactsFilterButton"
          :icon="isSegmentsView ? 'i-lucide-pen-line' : 'i-lucide-list-filter'"
          color="slate"
          size="sm"
          class="relative !w-8 !h-8"
          variant="ghost"
          @click="emit('filter')"
        >
          <div
            v-if="hasActiveFilters && !isSegmentsView"
            class="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-n-brand"
          />
        </Button>
        <slot name="filter" />
      </div>
      <Button
        v-if="
          hasActiveFilters && !isSegmentsView && !isLabelView && !isActiveView
        "
        icon="i-lucide-save"
        color="slate"
        size="sm"
        variant="ghost"
        class="!w-8 !h-8"
        @click="emit('createSegment')"
      />
      <Button
        v-if="isSegmentsView && !isLabelView && !isActiveView"
        icon="i-lucide-trash"
        color="slate"
        size="sm"
        variant="ghost"
        class="!w-8 !h-8"
        @click="emit('deleteSegment')"
      />
      <ContactSortMenu
        :active-sort="activeSort"
        :active-ordering="activeOrdering"
        @update:sort="emit('update:sort', $event)"
      />
      <ContactMoreActions
        @add="emit('add')"
        @import="emit('import')"
        @export="emit('export')"
      />
    </div>
  </header>
</template>
